import React from "react";
import { useForm, Control, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import TimesIcon from "@assets/img/times-icon.svg";
import { theme } from "@src/theme";
import Spinner from "@src/components/Spinner";
import { PlaylistStatus } from "@src/types/playlist";
import {
  CloseDrawerButton,
  SubmitDrawerButton,
  TopButtonsGroup,
} from "@src/components/ContentDrawer/DrawerComponents";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { RootState } from "@src/redux/store";
import Tooltip from "@src/components/Tooltip";
import {
  ILibraryFilterForm,
  setLibraryFilters,
  resetFilters,
} from "@src/redux/slices/librarySlice";
import TracksFilterFields from "@src/components/TracksFilterFields/TracksFilterFields";

const schema = yup
  .object({
    title: yup.string().optional(),
    artist: yup.string().optional(),
    album: yup.string().optional(),
    label: yup.string().optional(),
    bpm: yup.number().optional(),
    key: yup.string().optional(),
    durationInMinutes: yup.number().optional(),
    isTopLikedThisWeek: yup.boolean().optional(),
    missingYoutube: yup.boolean().optional(),
    missingSpotify: yup.boolean().optional(),
    missingApple: yup.boolean().optional(),
    missingShazam: yup.boolean().optional(),
    createdDateRange: yup
      .object()
      .shape({
        startDate: yup.string().optional(),
        endDate: yup.string().optional(),
      })
      .optional()
      .nullable(),
    scheduleDateRange: yup
      .object()
      .shape({
        startDate: yup.string().optional(),
        endDate: yup.string().optional(),
      })
      .optional()
      .nullable(),
  })
  .required();
export type FilterTracksFormValues = {
  showId?: string;
  playlistName?: string;
  description?: string;
  lastModified?: {
    startDate?: Date;
    endDate?: Date;
  };
  durationInMinutes?: number;
  guestId?: string;
  playlistStatus?: PlaylistStatus;
  isTopLikedThisWeek?: boolean;
};
type FilterTracksFormProps = {
  closeButtonHandler: () => void;
  submitButtonHandler: (data: ILibraryFilterForm) => Promise<void>;
  fetchingTracks: boolean;
  role?: string;
};
const defaultValues: ILibraryFilterForm = {
  title: "",
  artist: "",
  album: "",
  label: "",
  bpm: 0,
  key: "",
  durationInMinutes: 0,
  includedTags: [],
  excludedTags: [],
  includedMoods: [],
  excludedMoods: [],
  isTopLikedThisWeek: false,
  missingYoutube: false,
  missingSpotify: false,
  missingApple: false,
  missingShazam: false,
  createdDateRange: undefined,
  scheduleDateRange: undefined,
};
const FilterTracksForm: React.FC<FilterTracksFormProps> = ({
  fetchingTracks,
  closeButtonHandler,
  submitButtonHandler,
  role,
}) => {
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state: RootState) => state.libraryPage);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: filters || defaultValues,
  });

  const hasActiveFilters = React.useMemo(() => {
    if (!filters) return false;
    return (
      filters.title !== "" ||
      filters.artist !== "" ||
      filters.album !== "" ||
      filters.label !== "" ||
      filters.bpm !== 0 ||
      filters.key !== "" ||
      filters.durationInMinutes !== 0 ||
      filters.isTopLikedThisWeek !== false ||
      (filters.includedTags && filters.includedTags.length > 0) ||
      (filters.excludedTags && filters.excludedTags.length > 0) ||
      (filters.includedMoods && filters.includedMoods.length > 0) ||
      (filters.excludedMoods && filters.excludedMoods.length > 0) ||
      filters.missingYoutube !== false ||
      filters.missingSpotify !== false ||
      filters.missingApple !== false ||
      filters.missingShazam !== false ||
      filters.createdDateRange !== undefined ||
      filters.scheduleDateRange !== undefined
    );
  }, [filters]);

  const onSubmit = async (data: FilterTracksFormValues): Promise<void> => {
    if (fetchingTracks) return;
    dispatch(setLibraryFilters(data as ILibraryFilterForm));
    await submitButtonHandler(data as ILibraryFilterForm);
  };

  const resetFiltersCb = () => {
    reset(defaultValues);
    dispatch(resetFilters());
  };
  const onClose = async () => {
    closeButtonHandler();
    dispatch(resetFilters());
    await submitButtonHandler(defaultValues as ILibraryFilterForm);
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <TopButtonsGroup>
        <CloseDrawerButton type="button" onClick={onClose}>
          <img
            src={TimesIcon}
            alt="X"
            style={{ width: "10px", height: "10px" }}
          />
        </CloseDrawerButton>
        {fetchingTracks && <Spinner small />}
        <div style={{ display: "flex", gap: theme.margins.medium }}>
          {(isDirty || hasActiveFilters) && (
            <Tooltip content={"Clear filters"} position="top">
              <SubmitDrawerButton
                type="button"
                onClick={resetFiltersCb}
                disabled={fetchingTracks}
              >
                Reset filters
              </SubmitDrawerButton>
            </Tooltip>
          )}
          <Tooltip content={"Submit filters"} position="top">
            <SubmitDrawerButton disabled={fetchingTracks || !isDirty}>
              Save filters
            </SubmitDrawerButton>
          </Tooltip>
        </div>
      </TopButtonsGroup>

      <TracksFilterFields
        control={control as unknown as Control<FieldValues>}
        errors={errors}
        role={role}
      />
    </form>
  );
};

export default FilterTracksForm;
